import { motion, AnimatePresence, useInView } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";

const EASE = [0.16, 1, 0.3, 1] as const;

const FOREST = "#0A4A3E";
const FOREST_DEEP = "#052E27";
const LEAF = "#8FBE72";
const SAGE = "#C8DFB8";
const MINT = "#BFE5D7";
const GOLD = "#E7B84B";
const PAPER = "#FCFDF8";
const WHITE = "#FFFFFF";
const INK = "#123D35";
const MUTED = "#60756C";
const LINE = "#D3E0CC";
const FS = "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif";

type Tab = "parents" | "schools";

const iconPaths: Record<string, string> = {
  bus: "M5 11l1-4h12l1 4M5 11h14v6H5zM8 17v2M16 17v2",
  check: "M5 12l5 5L20 7",
  book: "M2 3h20v14H2zM8 21h8M12 17v4",
  meal: "M12 2a9 9 0 0 0-9 9h18a9 9 0 0 0-9-9zM3 13a9 9 0 0 0 18 0M12 13v8",
  star: "M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z",
  wallet: "M3 6h18v12H3zM3 10h18",
  message: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  shield: "M12 2l9 4v6c0 5-3.5 9-9 10-5.5-1-9-5-9-10V6z",
  building: "M3 21h18M5 21V7l8-4v18M19 21V11l-6-4",
  gear: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  doc: "M14 2H6v20h12V8zM14 2v6h6",
  activity: "M3 12h4l3-7 4 14 3-7h4",
  home: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
  chart: "M3 3v18h18M7 14l4-4 4 4 5-5",
};

function Icon({ type, size = 18, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={iconPaths[type] ?? iconPaths.check} /></svg>;
}

const parentDayEvents = [
  { time: "6:45 AM", title: "Bus approaching", desc: "Bus 12 is 4 minutes away. Your child will be picked up at Naalya stop.", color: LEAF, icon: "bus" },
  { time: "7:22 AM", title: "Arrived at school", desc: "Sarah checked in via biometric. Attendance confirmed.", color: FOREST, icon: "check" },
  { time: "8:00 AM", title: "First lesson", desc: "Mathematics with Mr. Okello. Class started on time.", color: GOLD, icon: "book" },
  { time: "10:30 AM", title: "Lunch purchased", desc: "UGX 5,000 — Rice & beef stew. Wallet: UGX 37,000.", color: MINT, icon: "meal" },
  { time: "12:15 PM", title: "Science test result", desc: "Sarah scored 92%. Above class average of 78%.", color: GOLD, icon: "star" },
  { time: "3:30 PM", title: "Football practice", desc: "90-min session. Heart rate avg: 142 bpm.", color: LEAF, icon: "activity" },
  { time: "4:05 PM", title: "Bus departed", desc: "Sarah boarded Bus 12. ETA: 4:35 PM.", color: MINT, icon: "bus" },
  { time: "4:35 PM", title: "Arrived home", desc: "Sarah arrived safely. You've been notified.", color: LEAF, icon: "home" },
];

const parentFeatures = [
  { title: "Pay fees instantly", desc: "Mobile money, card, or wallet — receipts generated automatically.", color: GOLD, icon: "wallet" },
  { title: "Track the bus live", desc: "Know exactly when your child boards and arrives.", color: LEAF, icon: "bus" },
  { title: "See academic progress", desc: "Grades, attendance, and teacher feedback in real time.", color: GOLD, icon: "star" },
  { title: "Control spending", desc: "Sub-wallets for meals, books, and transport — with limits.", color: MINT, icon: "shield" },
  { title: "Sign documents digitally", desc: "Consent forms, report cards — paperless.", color: FOREST, icon: "doc" },
  { title: "Message teachers directly", desc: "Unified messaging across app, SMS, and WhatsApp.", color: LEAF, icon: "message" },
];

const parentStats = [
  { value: "8", label: "Touchpoints", desc: "Every part of the school day", color: LEAF },
  { value: "1", label: "View", desc: "One child, one unified experience", color: GOLD },
  { value: "0", label: "Systems to juggle", desc: "Everything connected", color: FOREST },
  { value: "24/7", label: "Visibility", desc: "From morning bus to evening arrival", color: MINT },
];

const schoolModules = [
  { name: "Administration", desc: "Admissions, enrollment, staff, documents.", color: FOREST, icon: "building", items: ["Admissions", "Enrollment", "Staff", "Documents"] },
  { name: "Academics", desc: "Timetables, attendance, grades, assessments.", color: GOLD, icon: "book", items: ["Timetables", "Attendance", "Grades", "Assessments"] },
  { name: "Treasury", desc: "Fees, billing, collections, reconciliation.", color: GOLD, icon: "wallet", items: ["Fees", "Billing", "Collections", "Reconciliation"] },
  { name: "Procurement", desc: "Suppliers, approvals, orders, inventory.", color: LEAF, icon: "chart", items: ["Suppliers", "Approvals", "Orders", "Inventory"] },
  { name: "School Feeding", desc: "Meal planning, menus, cost tracking.", color: LEAF, icon: "meal", items: ["Planning", "Menus", "Procurement", "Cost"] },
  { name: "Transport", desc: "Routes, vehicles, drivers, live tracking.", color: MINT, icon: "bus", items: ["Routes", "Vehicles", "Drivers", "Live tracking"] },
  { name: "Communication", desc: "Announcements, messaging, alerts.", color: FOREST, icon: "message", items: ["Announcements", "Messaging", "Alerts", "Engagement"] },
  { name: "Intelligence", desc: "Signals, insights, recommendations.", color: GOLD, icon: "gear", items: ["Signal detection", "Insights", "Recommendations"] },
];

const schoolStats = [
  { value: "1,200", label: "Learners", color: FOREST },
  { value: "2,400", label: "Parents", color: LEAF },
  { value: "80", label: "Teachers", color: GOLD },
  { value: "12", label: "Buses", color: MINT },
];

const schoolFeedEvents = [
  { time: "07:44", title: "Arrival surge", desc: "847 learners checked in within 20 minutes.", color: LEAF, icon: "check" },
  { time: "09:15", title: "Treasury alert", desc: "UGX 4.2M collected overnight via mobile money.", color: GOLD, icon: "wallet" },
  { time: "11:30", title: "Feeding milestone", desc: "1,180 meals served. Stock supports 4.2 more days.", color: MINT, icon: "meal" },
  { time: "13:00", title: "Procurement signal", desc: "Maize flour 14% above recent average.", color: FOREST, icon: "chart" },
  { time: "14:45", title: "Academic update", desc: "S2 Science assessment submitted. 89% completion.", color: GOLD, icon: "book" },
  { time: "15:50", title: "Transport dispatch", desc: "12 buses dispatched. All drivers verified.", color: LEAF, icon: "bus" },
];

export function ExperiencePage() {
  const [tab, setTab] = useState<Tab>("parents");
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: PAPER, color: INK, fontFamily: FS }}>
      <div className="absolute top-0 left-0 right-0 z-50 h-[3px]" style={{ background: `linear-gradient(90deg, ${FOREST}, ${LEAF}, ${GOLD}, ${MINT}, ${FOREST})` }} />
      <div className="relative z-10">
        <Navbar />
        <ExperienceHero tab={tab} setTab={setTab} />
        <AnimatePresence mode="wait">
          {tab === "parents" ? (
            <motion.div key="parents" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <ParentStatsSection />
              <ParentTimelineSection />
              <ParentFeaturesSection />
              <ParentCTASection />
            </motion.div>
          ) : (
            <motion.div key="schools" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <SchoolStatsSection />
              <SchoolFeedSection />
              <SchoolBentoSection />
              <SchoolCTASection />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Footer />
    </div>
  );
}

function ExperienceHero({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <section className="relative overflow-hidden px-4 pb-12 pt-28 sm:px-8 sm:pt-36 sm:pb-20" style={{ background: FOREST_DEEP }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(${WHITE}44 1px, transparent 1px), linear-gradient(90deg, ${WHITE}44 1px, transparent 1px)`, backgroundSize: "48px 48px" }} />
      <motion.div animate={{ x: [0, 20, 0], y: [0, -15, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute left-10 top-20 h-40 w-40 rounded-full opacity-20 blur-3xl" style={{ background: tab === "parents" ? LEAF : MINT }} />
      <motion.div animate={{ x: [0, -15, 0], y: [0, 10, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="pointer-events-none absolute right-10 bottom-10 h-48 w-48 rounded-full opacity-15 blur-3xl" style={{ background: tab === "parents" ? GOLD : LEAF }} />

      <div className="relative mx-auto max-w-6xl px-4">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mb-8 flex flex-wrap gap-3">
          <TabPill active={tab === "parents"} onClick={() => setTab("parents")} label="For Parents" color={LEAF} />
          <TabPill active={tab === "schools"} onClick={() => setTab("schools")} label="For Schools" color={MINT} />
        </motion.div>
        <AnimatePresence mode="wait">
          {tab === "parents" ? (
            <motion.div key="p-hero" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4, ease: EASE }}>
              <Eyebrow color={LEAF} text="One child. One view." />
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-6xl" style={{ color: PAPER, letterSpacing: "-0.04em", lineHeight: 0.98 }}>Every moment of your child's</h2>
              <h2 className="text-3xl font-bold tracking-tight sm:text-6xl" style={{ color: LEAF, letterSpacing: "-0.04em", lineHeight: 0.98, fontStyle: "italic" }}>education. In your pocket.</h2>
            </motion.div>
          ) : (
            <motion.div key="s-hero" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4, ease: EASE }}>
              <Eyebrow color={MINT} text="The engine of the ecosystem" />
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-6xl" style={{ color: PAPER, letterSpacing: "-0.04em", lineHeight: 0.98 }}>Run everything from one</h2>
              <h2 className="text-3xl font-bold tracking-tight sm:text-6xl" style={{ color: MINT, letterSpacing: "-0.04em", lineHeight: 0.98, fontStyle: "italic" }}>operating system.</h2>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function TabPill({ active, onClick, label, color }: { active: boolean; onClick: () => void; label: string; color: string }) {
  return (
    <button onClick={onClick} className="group relative flex items-center gap-2 rounded-2xl px-4 py-3 text-left transition-all duration-300 sm:px-5" style={{ minWidth: 150, background: active ? color : "rgba(255,255,255,0.06)", color: active ? FOREST_DEEP : "rgba(255,255,255,0.7)", border: `1.5px solid ${active ? color : "rgba(255,255,255,0.12)"}`, boxShadow: active ? `0 10px 30px ${color}30` : "none" }}>
      <span className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: active ? `${FOREST_DEEP}12` : "rgba(255,255,255,0.08)" }}>
        <motion.span animate={{ scale: active ? 1.2 : 1 }} className="h-2 w-2 rounded-full" style={{ background: active ? FOREST_DEEP : color }} />
      </span>
      <span><span className="block text-[10px] uppercase tracking-[0.2em] opacity-60">View</span><span className="block text-sm font-bold">{label}</span></span>
    </button>
  );
}

function Eyebrow({ color, text }: { color: string; text: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)" }}>
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.6)" }}>{text}</span>
    </div>
  );
}

function SectionLabel({ color, text }: { color: string; text: string }) {
  return (
    <div className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5" style={{ borderColor: `${color}25`, background: `${color}08` }}>
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color }}>{text}</span>
    </div>
  );
}

// ═══ PARENT SECTIONS ═══
function ParentStatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: WHITE }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 0%, ${LEAF}08 0%, transparent 50%)` }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {parentStats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}} transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }} whileHover={{ y: -4 }} className="group relative overflow-hidden rounded-2xl border p-5" style={{ borderColor: `${LINE}80`, background: PAPER }}>
              <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: `radial-gradient(circle at 50% 50%, ${s.color}08 0%, transparent 70%)` }} />
              <div className="relative">
                <div className="text-3xl font-bold tabular-nums sm:text-5xl" style={{ color: s.color, letterSpacing: "-0.04em" }}>{s.value}</div>
                <div className="mt-2 text-sm font-bold" style={{ color: INK }}>{s.label}</div>
                <div className="mt-1 text-[11px] leading-relaxed" style={{ color: MUTED }}>{s.desc}</div>
              </div>
              <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full" style={{ background: s.color }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ParentTimelineSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => setActiveStep(s => (s + 1) % parentDayEvents.length), 2500);
    return () => clearInterval(id);
  }, [autoPlay]);

  const active = parentDayEvents[activeStep];

  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: PAPER }}>
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <SectionLabel color={LEAF} text="A Day in the Life" />
        <motion.h2 initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>Sarah's school day, <span style={{ color: LEAF, fontStyle: "italic" }}>in real time.</span></motion.h2>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="relative">
            <div className="absolute bottom-2 left-6 top-2 w-0.5 rounded-full" style={{ background: `${LINE}80` }} />
            <motion.div className="absolute left-6 w-0.5 rounded-full" style={{ background: `linear-gradient(to bottom, ${LEAF}, ${FOREST}, ${GOLD}, ${MINT})` }} animate={{ height: `${((activeStep + 1) / parentDayEvents.length) * 100}%`, top: 0 }} transition={{ duration: 0.5, ease: EASE }} />
            <div className="space-y-3">
              {parentDayEvents.map((e, i) => (
                <button key={i} onClick={() => { setActiveStep(i); setAutoPlay(false); }} className="relative flex w-full items-start gap-3 text-left">
                  <motion.div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border-2 z-10" style={{ borderColor: i <= activeStep ? e.color : `${LINE}80`, background: i === activeStep ? e.color : i < activeStep ? `${e.color}15` : WHITE }} animate={{ scale: i === activeStep ? 1.1 : 1 }} whileHover={{ scale: 1.1 }}>
                    <Icon type={e.icon} size={16} color={i <= activeStep ? WHITE : MUTED} />
                  </motion.div>
                  <motion.div className="flex-1 rounded-xl border p-3 transition-all" style={{ borderColor: i === activeStep ? `${e.color}40` : `${LINE}80`, background: i === activeStep ? `${e.color}08` : WHITE, boxShadow: i === activeStep ? `0 4px 16px ${e.color}10` : "none" }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-bold tabular-nums" style={{ color: i === activeStep ? e.color : MUTED }}>{e.time}</span>
                      {i === activeStep && <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="h-1.5 w-1.5 rounded-full" style={{ background: e.color }} />}
                    </div>
                    <p className="mt-1 text-xs font-semibold" style={{ color: i === activeStep ? INK : MUTED }}>{e.title}</p>
                    <AnimatePresence>
                      {i === activeStep && (
                        <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden text-[11px] leading-relaxed" style={{ color: MUTED }}>{e.desc}</motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </button>
              ))}
            </div>
          </div>
          <div className="hidden lg:block"><PhoneMockup active={active} step={activeStep} total={parentDayEvents.length} /></div>
        </div>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={() => setAutoPlay(!autoPlay)} className="flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-all hover:scale-105" style={{ borderColor: `${LINE}80`, color: MUTED, background: WHITE }}>
            <motion.span animate={{ scale: autoPlay ? [1, 1.3, 1] : 1 }} transition={{ duration: 1.5, repeat: autoPlay ? Infinity : 0 }} className="h-2 w-2 rounded-full" style={{ background: autoPlay ? LEAF : MUTED }} />
            {autoPlay ? "Auto-playing" : "Paused"}
          </button>
        </div>
      </div>
    </section>
  );
}

function PhoneMockup({ active, step, total }: { active: typeof parentDayEvents[0]; step: number; total: number }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="sticky top-24 mx-auto w-[280px]">
      <div className="relative rounded-[2.5rem] border-[6px] p-3 shadow-2xl" style={{ borderColor: FOREST_DEEP, background: FOREST_DEEP }}>
        <div className="absolute left-1/2 top-0 h-6 w-32 -translate-x-1/2 rounded-b-2xl" style={{ background: FOREST_DEEP }} />
        <div className="overflow-hidden rounded-[2rem]" style={{ background: FOREST_DEEP }}>
          <div className="flex items-center justify-between px-5 pt-6 pb-2 text-[9px] font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>
            <span>9:41</span><div className="flex items-center gap-1"><span>5G</span><span>100%</span></div>
          </div>
          <div className="px-5 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold" style={{ color: PAPER }}>Sarah's Day</span>
              <div className="flex items-center gap-1.5 rounded-full px-2 py-0.5" style={{ background: `${active.color}15` }}>
                <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="h-1.5 w-1.5 rounded-full" style={{ background: active.color }} />
                <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: active.color }}>Live</span>
              </div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3, ease: EASE }} className="mx-3 mb-3 rounded-2xl p-4" style={{ background: `${active.color}15` }}>
              <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${active.color}25` }}><Icon type={active.icon} size={24} color={active.color} /></div>
              <p className="mt-3 text-[10px] font-bold tabular-nums" style={{ color: active.color }}>{active.time}</p>
              <p className="mt-1 text-sm font-bold" style={{ color: PAPER }}>{active.title}</p>
              <p className="mt-2 text-[10px] leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>{active.desc}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex justify-center gap-1 pb-6">
            {Array.from({ length: total }).map((_, i) => <div key={i} className="h-1 rounded-full transition-all" style={{ background: i === step ? active.color : `${MUTED}40`, width: i === step ? 16 : 6 }} />)}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ParentFeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: WHITE }}>
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <SectionLabel color={GOLD} text="Parent Capabilities" />
        <motion.h2 initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>Everything you need, <span style={{ color: GOLD, fontStyle: "italic" }}>nothing you don't.</span></motion.h2>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {parentFeatures.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.08, duration: 0.5, ease: EASE }} whileHover={{ y: -4 }} className="group relative overflow-hidden rounded-2xl border p-5" style={{ borderColor: `${LINE}80`, background: PAPER }}>
              <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: `linear-gradient(135deg, ${f.color}08 0%, transparent 60%)` }} />
              <div className="relative">
                <div className="mb-3 grid h-12 w-12 place-items-center rounded-xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: `${f.color}15` }}><Icon type={f.icon} size={24} color={f.color} /></div>
                <h3 className="text-base font-bold" style={{ color: INK, letterSpacing: "-0.02em" }}>{f.title}</h3>
                <p className="mt-1 text-xs leading-relaxed" style={{ color: MUTED }}>{f.desc}</p>
              </div>
              <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full" style={{ background: f.color }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ParentCTASection() {
  return (
    <section className="relative overflow-hidden py-16 sm:py-24" style={{ background: FOREST_DEEP }}>
      <motion.div animate={{ background: [`linear-gradient(135deg, ${LEAF}12, ${GOLD}08)`, `linear-gradient(135deg, ${GOLD}12, ${LEAF}08)`, `linear-gradient(135deg, ${LEAF}12, ${GOLD}08)`] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute inset-0" />
      {[...Array(6)].map((_, i) => <motion.div key={i} className="pointer-events-none absolute h-1 w-1 rounded-full" style={{ background: LEAF, left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%` }} animate={{ y: [0, -20, 0], opacity: [0, 0.6, 0] }} transition={{ duration: 4, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }} />)}
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: PAPER, letterSpacing: "-0.04em", lineHeight: 0.92 }}>Everything a parent needs.</h2>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: LEAF, letterSpacing: "-0.04em", lineHeight: 0.92, fontStyle: "italic" }}>One experience.</h2>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-full px-8 text-[13px] font-bold uppercase tracking-[0.15em]" style={{ background: LEAF, color: FOREST_DEEP, border: "none" }}><Link to="/auth">Get Started</Link></Button>
            <Button asChild variant="ghost" size="lg" className="h-12 rounded-full px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white" style={{ border: "1.5px solid rgba(255,255,255,0.15)" }}><Link to="/experience">Learn More</Link></Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ═══ SCHOOL SECTIONS ═══
function SchoolStatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: WHITE }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 0%, ${MINT}08 0%, transparent 50%)` }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {schoolStats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: i * 0.08, duration: 0.4, ease: EASE }} whileHover={{ y: -4, scale: 1.05 }} className="group relative overflow-hidden rounded-2xl border p-5 text-center" style={{ borderColor: `${LINE}80`, background: PAPER }}>
              <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: `radial-gradient(circle at 50% 50%, ${s.color}10 0%, transparent 70%)` }} />
              <div className="relative">
                <div className="text-2xl font-bold tabular-nums sm:text-4xl" style={{ color: s.color, letterSpacing: "-0.04em" }}>{s.value}</div>
                <div className="mt-1.5 text-[10px] font-medium uppercase tracking-wider" style={{ color: MUTED }}>{s.label}</div>
              </div>
              <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full" style={{ background: s.color }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SchoolFeedSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    const id = setInterval(() => setActiveIdx(i => (i + 1) % schoolFeedEvents.length), 3000);
    return () => clearInterval(id);
  }, []);

  const active = schoolFeedEvents[activeIdx];

  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: PAPER }}>
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <SectionLabel color={MINT} text="Live Campus Feed" />
        <motion.h2 initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>See your campus <span style={{ color: MINT, fontStyle: "italic" }}>in real time.</span></motion.h2>
        <div className="mt-10 grid gap-4 lg:grid-cols-[1fr_1.5fr]">
          <div className="space-y-2">
            {schoolFeedEvents.map((e, i) => (
              <button key={i} onClick={() => setActiveIdx(i)} className="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all" style={{ borderColor: i === activeIdx ? e.color : `${LINE}80`, background: i === activeIdx ? `${e.color}08` : WHITE, boxShadow: i === activeIdx ? `0 4px 16px ${e.color}08` : "none", transform: i === activeIdx ? "translateX(4px)" : "none" }}>
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg" style={{ background: `${e.color}15` }}><Icon type={e.icon} size={18} color={e.color} /></div>
                <div className="min-w-0 flex-1"><p className="text-xs font-bold" style={{ color: i === activeIdx ? INK : MUTED }}>{e.title}</p><p className="text-[10px] tabular-nums" style={{ color: MUTED }}>{e.time}</p></div>
                <motion.div animate={{ scale: i === activeIdx ? 1.2 : 1 }} className="h-2 w-2 rounded-full" style={{ background: i === activeIdx ? e.color : "transparent" }} />
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={activeIdx} initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }} transition={{ duration: 0.3, ease: EASE }} className="relative overflow-hidden rounded-2xl border p-6" style={{ borderColor: `${active.color}25`, background: WHITE, boxShadow: `0 8px 32px ${active.color}12` }}>
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: active.color }} />
              <div className="flex items-center gap-3">
                <motion.div className="grid h-14 w-14 place-items-center rounded-xl" style={{ background: `${active.color}15` }} animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}><Icon type={active.icon} size={28} color={active.color} /></motion.div>
                <div><p className="text-lg font-bold" style={{ color: INK }}>{active.title}</p><p className="text-[11px] tabular-nums" style={{ color: MUTED }}>{active.time}</p></div>
              </div>
              <p className="mt-4 text-sm leading-relaxed" style={{ color: MUTED }}>{active.desc}</p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5" style={{ background: `${active.color}10` }}>
                <motion.span className="h-2 w-2 rounded-full" style={{ background: active.color }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: active.color }}>Live signal</span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function SchoolBentoSection() {
  const [expanded, setExpanded] = useState<number | null>(2);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const bentoSpans = ["lg:col-span-2", "lg:col-span-1", "lg:col-span-1", "lg:col-span-2", "lg:col-span-1", "lg:col-span-1", "lg:col-span-2", "lg:col-span-1"];

  return (
    <section ref={ref} className="relative py-16 sm:py-24" style={{ background: WHITE }}>
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <SectionLabel color={LEAF} text="Eight Campus Modules" />
        <motion.h2 initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>One connected <span style={{ color: LEAF, fontStyle: "italic" }}>campus.</span></motion.h2>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {schoolModules.map((m, i) => {
            const isExpanded = expanded === i;
            return (
              <motion.div key={m.name} initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.05, duration: 0.4, ease: EASE }} whileHover={{ y: -2 }} onClick={() => setExpanded(isExpanded ? null : i)} className={`group relative cursor-pointer overflow-hidden rounded-2xl border transition-all duration-300 ${bentoSpans[i] || "lg:col-span-1"}`} style={{ borderColor: isExpanded ? m.color : `${LINE}80`, background: isExpanded ? `${m.color}08` : PAPER, boxShadow: isExpanded ? `0 8px 24px ${m.color}10` : "none" }}>
                <div className="absolute top-0 left-0 right-0 h-0.5 transition-all duration-300" style={{ background: m.color, opacity: isExpanded ? 1 : 0 }} />
                <div className="flex items-center gap-3 p-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-transform duration-300 group-hover:scale-110" style={{ background: `${m.color}15` }}><Icon type={m.icon} size={22} color={m.color} /></div>
                  <div className="flex-1"><h3 className="text-sm font-bold" style={{ color: INK }}>{m.name}</h3><p className="text-[11px]" style={{ color: MUTED }}>{m.desc}</p></div>
                  <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}><svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke={isExpanded ? m.color : MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg></motion.div>
                </div>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden">
                      <div className="border-t p-4" style={{ borderColor: `${m.color}15` }}>
                        <div className="flex flex-wrap gap-2">{m.items.map(item => <span key={item} className="rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-all hover:scale-105" style={{ borderColor: `${m.color}20`, background: `${m.color}06`, color: m.color }}>{item}</span>)}</div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SchoolCTASection() {
  return (
    <section className="relative overflow-hidden py-16 sm:py-24" style={{ background: FOREST_DEEP }}>
      <motion.div animate={{ background: [`linear-gradient(135deg, ${MINT}12, ${LEAF}08)`, `linear-gradient(135deg, ${LEAF}12, ${MINT}08)`, `linear-gradient(135deg, ${MINT}12, ${LEAF}08)`] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute inset-0" />
      {[...Array(6)].map((_, i) => <motion.div key={i} className="pointer-events-none absolute h-1 w-1 rounded-full" style={{ background: MINT, left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%` }} animate={{ y: [0, -20, 0], opacity: [0, 0.6, 0] }} transition={{ duration: 4, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }} />)}
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: PAPER, letterSpacing: "-0.04em", lineHeight: 0.92 }}>The school becomes the</h2>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: MINT, letterSpacing: "-0.04em", lineHeight: 0.92, fontStyle: "italic" }}>operational heart.</h2>
          <p className="mt-4 text-sm sm:text-lg" style={{ color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>Eight modules. One campus. Zero disconnects.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-full px-8 text-[13px] font-bold uppercase tracking-[0.15em]" style={{ background: MINT, color: FOREST_DEEP, border: "none" }}><Link to="/auth">Get Started</Link></Button>
            <Button asChild variant="ghost" size="lg" className="h-12 rounded-full px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white" style={{ border: "1.5px solid rgba(255,255,255,0.15)" }}><Link to="/auth">Talk to Fundred</Link></Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ExperiencePage;
